import 'easyzoom';
import clone from 'lodash/clone';

/**
 * Precedence rule for the main image's alt/title text (Fix 2, round 2):
 * an alt the caller already knows about (a thumbnail's own alt, carried by
 * selectNewImage/restoreImage) always wins; failing that, an alt matched by
 * URL to a visible thumbnail (resolveMainImageAlt's fallback for the
 * setAlternateImage/showProductImage path, which has no alt of its own); and
 * if neither is available, '' - the caller must leave the previous alt/title
 * alone rather than write something wrong. Pure and DOM-free so it can be
 * unit tested without a jQuery/easyZoom fixture.
 */
export function pickMainImageAlt(knownAlt, matchedAlt) {
    return knownAlt || matchedAlt || '';
}

export default class ImageGallery {
    constructor($gallery) {
        this.$root = $gallery;
        this.$mainImage = $gallery.find('[data-image-gallery-main]');
        this.$selectableImages = $gallery.find('[data-image-gallery-item]');
        this.currentImage = {};
    }

    init() {
        this.bindEvents();
        this.setImageZoom();
    }

    setMainImage(imgObj) {
        this.currentImage = clone(imgObj);

        this.setActiveThumb();
        this.swapMainImage();
    }

    setAlternateImage(imgObj) {
        if (!this.savedImage) {
            this.savedImage = {
                mainImageUrl: this.$mainImage.find('img').attr('src'),
                zoomImageUrl: this.$mainImage.attr('data-zoom-image'),
                mainImageSrcset: this.$mainImage.find('img').attr('srcset'),
                mainImageAlt: this.$mainImage.find('img').attr('alt'),
                $selectedThumb: this.currentImage.$selectedThumb,
            };
        }
        this.setMainImage(imgObj);
    }

    restoreImage() {
        if (this.savedImage) {
            this.setMainImage(this.savedImage);
            delete this.savedImage;
        }
    }

    selectNewImage(e) {
        e.preventDefault();
        const $target = $(e.currentTarget);
        const imgObj = {
            mainImageUrl: $target.attr('data-image-gallery-new-image-url'),
            zoomImageUrl: $target.attr('data-image-gallery-zoom-image-url'),
            mainImageSrcset: $target.attr('data-image-gallery-new-image-srcset'),
            // The thumbnail's own <img> already carries correct, clean alt text
            // from components/common/responsive-img.html (the "|| Option Value"
            // token is stripped server-side before it reaches alt). Carrying it
            // across keeps the main image's alt in sync with what's displayed -
            // see swapMainImage()/resolveMainImageAlt() below.
            mainImageAlt: $target.find('img').attr('alt'),
            $selectedThumb: $target,
        };

        this.setMainImage(imgObj);
    }

    setActiveThumb() {
        this.$selectableImages.removeClass('is-active');
        if (this.currentImage.$selectedThumb) {
            this.currentImage.$selectedThumb.addClass('is-active');
        }
    }

    swapMainImage() {
        this.easyzoom.data('easyZoom').swap(
            this.currentImage.mainImageUrl,
            this.currentImage.zoomImageUrl,
            this.currentImage.mainImageSrcset,
        );

        this.$mainImage.attr({
            'data-zoom-image': this.currentImage.zoomImageUrl,
        });

        // Keep alt/title tracking the image actually displayed. Without this,
        // a stale alt (e.g. left over from the server-rendered default option)
        // actively misdescribes the swapped-in image to assistive tech - see
        // resolveMainImageAlt() for how the alt is sourced when this call came
        // from a path with no alt of its own (product-details.js's
        // showProductImage/setAlternateImage). If neither source has one,
        // leave the existing alt/title alone rather than writing something
        // wrong.
        const alt = this.resolveMainImageAlt();
        if (alt) {
            this.$mainImage.find('img').attr({ alt, title: alt });
        }
    }

    /**
     * Sourcing order for the main image's alt text:
     * 1. `currentImage.mainImageAlt`, when the caller already knows it (a
     *    thumbnail click - selectNewImage - or a restored save - restoreImage).
     * 2. A thumbnail whose own new-image URL matches the image now on stage,
     *    for callers with only a URL (setAlternateImage's variant-image path
     *    from product-details.js's showProductImage, which has no alt to
     *    give). This is the same "does a visible thumbnail correspond to this
     *    src" match option-image-gallery.js's syncMainImage uses.
     * 3. Neither: return '' and leave the previous alt/title in place.
     */
    resolveMainImageAlt() {
        const mainImageUrl = this.currentImage.mainImageUrl || '';
        let matchedAlt = '';

        if (mainImageUrl) {
            const $match = this.$selectableImages.filter(
                (i, el) => el.getAttribute('data-image-gallery-new-image-url') === mainImageUrl,
            );
            if ($match.length) matchedAlt = $match.find('img').attr('alt') || '';
        }

        return pickMainImageAlt(this.currentImage.mainImageAlt, matchedAlt);
    }

    checkImage() {
        const containerHeight = $('.productView-image').height();
        const containerWidth = $('.productView-image').width();
        const height = this.easyzoom.data('easyZoom').$zoom.context.height;
        const width = this.easyzoom.data('easyZoom').$zoom.context.width;
        if (height < containerHeight || width < containerWidth) {
            this.easyzoom.data('easyZoom').hide();
        }
    }

    setImageZoom() {
        this.easyzoom = this.$mainImage.easyZoom({
            onShow: () => this.checkImage(),
            errorNotice: '',
            loadingNotice: '',
        });
    }

    bindEvents() {
        this.$selectableImages.on('click', this.selectNewImage.bind(this));
    }
}
